import { Key, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { List } from 'immutable';
import { deselectLabels } from '../../../../common/labels/labelActions';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import { removeLabelFromElements } from '../elementLabelActions';

type UseSelectedLabelDocumentEventsArgs = {
    containerRef: React.RefObject<HTMLDivElement | null>;
    selectedLabelId: string | undefined;
    selectedLabelsElementId: string | undefined;
    selectableElementId: string;
    canSelectLabels: boolean;
    element: ImMNElement;
    selectedKeys: Set<Key>;
    isSelectedLabelInThisContainer: boolean;
    isEditingLabelName: boolean;
};

/**
 * Handles document-level interactions while this container owns the selected label.
 */
export const useSelectedLabelDocumentEvents = ({
    containerRef,
    selectedLabelId,
    selectedLabelsElementId,
    selectableElementId,
    canSelectLabels,
    element,
    selectedKeys,
    isSelectedLabelInThisContainer,
    isEditingLabelName,
}: UseSelectedLabelDocumentEventsArgs) => {
    const dispatch = useDispatch();

    // Attach document handlers for selected-label outside clicks and deletion.
    useEffect(() => {
        if (!containerRef.current) return;
        if (!isSelectedLabelInThisContainer) return;

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target || containerRef.current?.contains(target)) return;

            const elementTarget = target instanceof Element ? target : target.parentElement;
            if (!elementTarget?.closest('.canvas-section') || elementTarget?.closest('.LabelElementPillsContainer'))
                return;

            dispatch(deselectLabels());
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (isEditingLabelName) return;
            if (selectedLabelsElementId !== selectableElementId) return;
            if (selectedKeys.size === 0) return;
            if (!canSelectLabels) return;
            if (!selectedLabelId) return;

            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                event.stopPropagation();
                dispatch(removeLabelFromElements(List([element]), selectedLabelId));
                dispatch(deselectLabels());
            }
        };

        document.addEventListener('click', handleOutsideClick, true);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('click', handleOutsideClick, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [
        dispatch,
        containerRef,
        selectedLabelId,
        selectedLabelsElementId,
        selectableElementId,
        canSelectLabels,
        element,
        isSelectedLabelInThisContainer,
        isEditingLabelName,
    ]);
};
